import axios from "axios";

function stripEnv(value) {
  return (value || "").trim().replace(/^["']|["']$/g, "").replace(/\/$/, "");
}

function resolveApiBase() {
  const serverUrl = stripEnv(process.env.REACT_APP_SERVER_URL);
  const supabaseUrl = stripEnv(process.env.REACT_APP_SUPABASE_URL) || "http://127.0.0.1:4001";

  const functionsBase = serverUrl.endsWith("/functions/v1")
    ? serverUrl
    : `${supabaseUrl}/functions/v1`;

  return functionsBase.endsWith("/api") ? functionsBase : `${functionsBase}/api`;
}

export const API = resolveApiBase();

/** Polite scrapers / link previews — weak signal, not security. */
function isLikelyBot() {
  if (typeof navigator === "undefined") return true;
  const ua = navigator.userAgent || "";
  return /bot|crawl|spider|slurp|facebookexternalhit|preview|headless|wget|curl|python-requests|scrapy/i
    .test(ua);
}

/**
 * Fire-and-forget Edge Function warmup (cold isolate).
 * Skips likely bots. Does not prime the browser CORS preflight cache — that
 * only fills on real cross-origin requests that trigger browser OPTIONS.
 * Server short-circuits GET / before full app boot so this stays cheap.
 */
export function warmupApi() {
  try {
    if (isLikelyBot()) return;
    fetch(`${API}/`, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

// ---- Token storage ----
const AUTH_TOKEN_KEY = "ln_auth_token_v1";
const AUTH_ORG_KEY = "ln_auth_org_v1";

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const tok = localStorage.getItem(AUTH_TOKEN_KEY);
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, config } = error || {};
    console.error("[API error]", {
      method: config?.method?.toUpperCase(),
      url: config?.url,
      status: response?.status,
      detail: response?.data?.detail,
      data: response?.data,
      message: error?.message,
    });
    return Promise.reject(error);
  }
);

export function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}
export function setAuthToken(t) {
  if (t) localStorage.setItem(AUTH_TOKEN_KEY, t);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
}
export function getCachedAccount() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_ORG_KEY) || "null");
  } catch {
    return null;
  }
}
export function setCachedAccount(o) {
  if (o) localStorage.setItem(AUTH_ORG_KEY, JSON.stringify(o));
  else localStorage.removeItem(AUTH_ORG_KEY);
}

// ---- Local guest identity (dancer profile prefill) ----
const KEY = "ln_identity_v1";
export function getIdentity() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}
export function setIdentity(obj) {
  localStorage.setItem(KEY, JSON.stringify(obj));
}
export function clearIdentity() {
  localStorage.removeItem(KEY);
}

/** Guest-only identity for API requests. Signed-in requests omit dancer_id (JWT owns identity). */
export function ownDancerIdParam(fallback) {
  if (getAuthToken()) return undefined;
  return fallback || getIdentity()?.dancer_id || undefined;
}

// ---- Legacy organizer dashboard tokens (kept for backwards compat with
// already-created events that pre-date the auth system) ----
const OKEY = "ln_org_tokens_v1";
export function setOrgToken(eventId, token) {
  const all = JSON.parse(localStorage.getItem(OKEY) || "{}");
  all[eventId] = token;
  localStorage.setItem(OKEY, JSON.stringify(all));
}
export function getOrgToken(eventId) {
  const all = JSON.parse(localStorage.getItem(OKEY) || "{}");
  return all[eventId] || null;
}

export const DEMO_TOKENS = {
  "salsa-fri": "demo-token-salsa",
  "bachata-sun": "demo-token-bachata",
};

// ---- API error helper (handles validation array detail safely) ----
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
